import { useEffect, useState, useCallback } from "react";
import { Calendar, FileText, ChevronLeft, MessageCircle, CheckCircle2, XCircle, Trash2, Loader2 } from "lucide-react";
import { getBatches, getBatch, deleteBatch, deleteBatchesBulk } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Toaster, toast } from "sonner";

export default function History() {
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, bulk: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getBatches();
      setBatches(data.batches || []);
    } catch {
      setBatches([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleView = async (id) => {
    try {
      const data = await getBatch(id);
      setSelectedBatch(data);
    } catch (e) {
      console.error(e);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = (checked) => {
    if (checked) setSelectedIds(new Set(batches.map((b) => b.id)));
    else setSelectedIds(new Set());
  };

  const handleDelete = async () => {
    const { id, bulk } = deleteConfirm;
    setDeleteConfirm({ open: false, id: null, bulk: false });
    try {
      if (bulk) {
        const ids = Array.from(selectedIds);
        await deleteBatchesBulk(ids);
        toast.success(`נמחקו ${ids.length} שליחות`);
        setSelectedIds(new Set());
      } else if (id) {
        await deleteBatch(id);
        toast.success("השליחה נמחקה");
      }
      await load();
    } catch (e) {
      toast.error("שגיאה במחיקה");
    }
  };

  if (selectedBatch) {
    return <BatchDetail batch={selectedBatch} onBack={() => setSelectedBatch(null)} />;
  }

  const allSelected = batches.length > 0 && batches.every((b) => selectedIds.has(b.id));

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6 animate-fade-in">
      <Toaster position="top-center" richColors dir="rtl" />

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">היסטוריה</h1>
          <p className="text-muted-foreground mt-1">רשימת כל השליחות שבוצעו</p>
        </div>
        {selectedIds.size > 0 && (
          <Button
            variant="destructive"
            onClick={() => setDeleteConfirm({ open: true, id: null, bulk: true })}
            data-testid="bulk-delete-button"
          >
            <Trash2 className="h-4 w-4 ml-2" />
            מחק {selectedIds.size} פריטים
          </Button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-border overflow-hidden" data-testid="history-list">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
            טוען...
          </div>
        ) : batches.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="text-muted-foreground">עדיין אין היסטוריה</p>
            <p className="text-xs text-stone-400 mt-1">התחילי לשלוח מהדשבורד שלך</p>
          </div>
        ) : (
          <>
            {/* Select all header */}
            <div className="px-4 py-3 border-b border-border bg-stone-50 flex items-center gap-3">
              <Checkbox
                checked={allSelected}
                onCheckedChange={(c) => toggleAll(c)}
                data-testid="select-all-history"
              />
              <span className="text-xs text-stone-500">
                {selectedIds.size > 0 ? `${selectedIds.size} נבחרו` : "בחרי הכל"}
              </span>
            </div>
            <div className="divide-y divide-border">
              {batches.map((b) => (
                <div
                  key={b.id}
                  className="p-4 hover:bg-stone-50 transition-colors flex items-center justify-between flex-wrap gap-3"
                  data-testid={`history-item-${b.id}`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Checkbox
                      checked={selectedIds.has(b.id)}
                      onCheckedChange={() => toggleSelect(b.id)}
                      data-testid={`history-checkbox-${b.id}`}
                    />
                    <div className="h-10 w-10 rounded-lg bg-brand-50 flex items-center justify-center shrink-0">
                      <FileText className="h-5 w-5 text-brand" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{b.filename}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                        <Calendar className="h-3 w-3" />
                        {new Date(b.created_at).toLocaleString("he-IL")}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{b.customer_count} לקוחות</Badge>
                    <Button variant="ghost" size="sm" onClick={() => handleView(b.id)} data-testid={`view-batch-${b.id}`}>
                      צפה
                      <ChevronLeft className="h-4 w-4 mr-1" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteConfirm({ open: true, id: b.id, bulk: false })}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-batch-${b.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <Dialog open={deleteConfirm.open} onOpenChange={(o) => !o && setDeleteConfirm({ open: false, id: null, bulk: false })}>
        <DialogContent dir="rtl" className="sm:max-w-md" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-right">מחיקת רשומות היסטוריה</DialogTitle>
            <DialogDescription className="text-right">
              {deleteConfirm.bulk
                ? `האם את בטוחה שברצונך למחוק ${selectedIds.size} פריטים? פעולה זו לא ניתנת לשחזור.`
                : "האם את בטוחה שברצונך למחוק את הרשומה הזו? פעולה זו לא ניתנת לשחזור."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row-reverse gap-2 sm:gap-2">
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-button">
              <Trash2 className="h-4 w-4 ml-2" />
              מחק
            </Button>
            <Button variant="outline" onClick={() => setDeleteConfirm({ open: false, id: null, bulk: false })}>
              ביטול
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BatchDetail({ batch, onBack }) {
  const messages = batch.messages || [];
  const byCustomer = {};
  messages.forEach((m) => (byCustomer[m.customer_id] = m));

  const sent = messages.filter((m) => m.whatsapp_ok).length;
  const failed = messages.length - sent;

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6 animate-fade-in">
      <Button variant="ghost" onClick={onBack} data-testid="back-button">
        <ChevronLeft className="h-4 w-4 rotate-180 ml-1" />
        חזרה לרשימה
      </Button>

      <div>
        <h1 className="font-heading text-3xl font-bold">{batch.filename}</h1>
        <p className="text-muted-foreground mt-1">
          {new Date(batch.created_at).toLocaleString("he-IL")} • {batch.customer_count} לקוחות
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard label="סה״כ לקוחות" value={batch.customer_count} />
        <StatCard label="נשלחו" value={sent} color="green" icon={CheckCircle2} />
        <StatCard label="נכשלו" value={failed} color="red" icon={XCircle} />
        <StatCard label="ממתינים" value={batch.customer_count - messages.length} color="stone" />
      </div>

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="font-heading font-semibold">פרטי לקוחות</h3>
        </div>
        <div className="divide-y divide-border">
          {batch.customers.map((c) => {
            const r = byCustomer[c.id];
            return (
              <div key={c.id} className="p-4 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-medium">{c.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {c.phone_e164 || c.phone_raw} • {Number(c.amount).toLocaleString("he-IL")} ₪
                    {r?.invoice_number && <> • חשבון #{r.invoice_number}</>}
                  </p>
                </div>
                <div>
                  {!r && <Badge variant="outline">לא נשלח</Badge>}
                  {r?.whatsapp_ok && (
                    <Badge className="bg-whatsapp text-white gap-1">
                      <MessageCircle className="h-3 w-3" /> נשלח
                    </Badge>
                  )}
                  {r && !r.whatsapp_ok && <Badge variant="destructive">נכשל</Badge>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, color = "brand", icon: Icon }) {
  const colors = {
    brand: "text-brand",
    green: "text-green-600",
    red: "text-red-600",
    stone: "text-stone-600",
  };
  return (
    <div className="bg-white rounded-xl border border-border p-4">
      <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
      <div className={`flex items-center gap-2 mt-2 ${colors[color]}`}>
        {Icon && <Icon className="h-5 w-5" />}
        <span className="text-2xl font-heading font-bold">{value}</span>
      </div>
    </div>
  );
}
